import { PRODUCT_NAME } from '../constants/brand';
import React, { useState } from 'react';
import { Box, ButtonBase, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { useNavigate } from 'react-router-dom';
import { tourStepCounts } from './OnboardingVisualTour';

const TEAL = '#29C0BB';
const TEAL_DARK = '#0F766E';

const tours = [
  { title: `${PRODUCT_NAME} overview`, description: `The main areas of ${PRODUCT_NAME}, your profile and payout setup, and where to start.`, steps: tourStepCounts.overview, href: '/dashboard?tour=preview', Icon: ExploreOutlinedIcon },
  { title: 'Client walkthrough', description: 'From a consultation to a report and sharing.', steps: tourStepCounts.patient, href: '/consultation?tour=patient&replay=1', Icon: RecordVoiceOverOutlinedIcon },
  { title: 'Home practice walkthrough', description: 'Prepare, review and share a home practice handout with your client.', steps: tourStepCounts.homePractice, href: '/consultation?tour=home-practice&replay=1', Icon: MenuBookOutlinedIcon },
];

// `inline`: render just the small "?" button (the sidebar footer places it next to the
// signed-in user); otherwise the original padded row under the navigation.
export default function TourReplayMenu({ drawerOpen = true, inline = false }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const button = (
    <Tooltip title="Tours — replay a walkthrough" placement="right">
      <IconButton
        aria-label="Tours"
        onClick={() => setOpen(true)}
        size={inline ? 'small' : 'medium'}
        sx={{ width: inline ? 32 : 40, height: inline ? 32 : 40, color: '#94A3B8', '&:hover': { color: TEAL, backgroundColor: 'rgba(41,192,187,.10)' } }}
      >
        <HelpOutlineIcon fontSize={inline ? 'small' : 'medium'} />
      </IconButton>
    </Tooltip>
  );
  return <>
    {/* Help, not a destination: a quiet "?" rather than another menu item. */}
    {inline
      ? button
      : <Box sx={{ display: 'flex', justifyContent: drawerOpen ? 'flex-start' : 'center', px: drawerOpen ? 2.5 : 0, py: 1.5 }}>{button}</Box>}
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" aria-labelledby="tour-replay-title" PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 18px 50px rgba(15,23,42,.28)' } }}>
      <Box sx={{ position: 'relative', p: 3, pb: 2 }}>
        <IconButton aria-label="Close" onClick={() => setOpen(false)} size="small" sx={{ position: 'absolute', top: 12, right: 12, color: '#64748B' }}><CloseRoundedIcon fontSize="small" /></IconButton>
        <Typography variant="caption" color={TEAL_DARK} fontWeight={800}>GUIDED TOURS</Typography>
        <Typography id="tour-replay-title" variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>Replay a walkthrough</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Walkthroughs use sample data. Nothing is recorded, saved or charged.</Typography>
      </Box>
      <Stack spacing={1.5} sx={{ px: 3, pb: 3, overflowY: 'auto', minHeight: 0 }}>
        {tours.map(({ title, description, steps, href, Icon }) => (
          <ButtonBase key={title} onClick={() => { setOpen(false); navigate(href); }} sx={{ width: '100%', p: 2, gap: 2, alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', border: '1px solid #E5E7EB', borderRadius: '12px', transition: 'border-color .15s, background-color .15s', '&:hover, &.Mui-focusVisible': { borderColor: TEAL, backgroundColor: 'rgba(41,192,187,.06)' } }}>
            <Box sx={{ flex: '0 0 44px', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', backgroundColor: 'rgba(41,192,187,.12)', color: TEAL_DARK }}><Icon /></Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>{description}</Typography>
              <Typography variant="caption" color={TEAL_DARK} fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>{steps} steps</Typography>
            </Box>
            <ChevronRightRoundedIcon sx={{ color: TEAL }} />
          </ButtonBase>
        ))}
      </Stack>
    </Dialog>
  </>;
}
